import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, People as PeopleIcon } from '@mui/icons-material';

const formatHeader = (name) =>
  name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

const isJsonType = (dataType) => (dataType || '').toLowerCase().includes('json');
const isNumberType = (dataType) =>
  ['integer', 'numeric', 'double', 'real', 'smallint', 'bigint', 'decimal'].some((type) =>
    (dataType || '').toLowerCase().includes(type)
  );
const isBooleanType = (dataType) => (dataType || '').toLowerCase().includes('boolean');

const normalizeEditValue = (value, dataType) => {
  if (isJsonType(dataType) && value && typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  if (value === null || value === undefined) return '';
  return value;
};

const coerceEditValue = (value, dataType) => {
  if (value === '') return null;
  if (isBooleanType(dataType)) return Boolean(value);
  if (isNumberType(dataType)) return Number(value);
  if (isJsonType(dataType)) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
  return value;
};

const HIDDEN_COLUMNS = new Set([
  'id',
  'created_at',
  'updated_at',
  'color',
  'description',
  'image_url',
  'is_live_event',
  'name'
]);

const QUANTITY_COLUMNS = new Set(['quantity', 'remaining_quantity']);
const COLUMN_ORDER = ['label', 'icon', 'probability', 'quantity', 'remaining_quantity', 'is_claimable', 'is_active'];
const DEFAULT_ICON_OPTIONS = ['🎁', '🏆', '🎉', '🍹', '🍰', '💎', '✨', '🎫', '😢'];

const LuckyDrawPrizes = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [primaryKey, setPrimaryKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPrize, setNewPrize] = useState({
    label: '',
    icon: '🎁',
    probability: 0,
    quantity: 1,
    remaining_quantity: 1,
    is_claimable: false,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [isLiveEvent, setIsLiveEvent] = useState(false);

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);


  const brandName = getCurrentBrand();
  const isMillenniumBrand = brandName && brandName.toLowerCase().includes('millennium');

  const fetchMembers = async () => {
    try {
      setMembersLoading(true);
      setMembersError(null);
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_MEMBERS}?brand=${brand}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch lucky draw members');
      }
      const data = await response.json();
      setMembers(data.rows || []);
    } catch (err) {
      console.error('Error fetching lucky draw members:', err);
      setMembersError(err.message);
    } finally {
      setMembersLoading(false);
    }
  };

  const openMembersDialog = () => {
    fetchMembers();
    setMembersDialogOpen(true);
  };

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatRegisterDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const [startDateInput, setStartDateInput] = useState(getTodayDateString());
  const [endDateInput, setEndDateInput] = useState(getTodayDateString());

  const [appliedStartDate, setAppliedStartDate] = useState(getTodayDateString());
  const [appliedEndDate, setAppliedEndDate] = useState(getTodayDateString());

  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = () => {
    setIsSearching(true);
    setAppliedStartDate(startDateInput);
    setAppliedEndDate(endDateInput);
    setTimeout(() => {
      setIsSearching(false);
    }, 450);
  };

  const filteredMembers = useMemo(() => {
    let result = members;

    if (appliedStartDate) {
      const [year, month, day] = appliedStartDate.split('-').map(Number);
      const start = new Date(year, month - 1, day, 0, 0, 0, 0);
      result = result.filter(m => m.created_at && new Date(m.created_at) >= start);
    }

    if (appliedEndDate) {
      const [year, month, day] = appliedEndDate.split('-').map(Number);
      const end = new Date(year, month - 1, day, 23, 59, 59, 999);
      result = result.filter(m => m.created_at && new Date(m.created_at) <= end);
    }

    return result;
  }, [members, appliedStartDate, appliedEndDate]);

  const visibleColumns = useMemo(() => {
    const filtered = columns.filter((column) => (
      column.name
      && !HIDDEN_COLUMNS.has(column.name)
      && (isLiveEvent || !QUANTITY_COLUMNS.has(column.name))
    ));
    const ordered = [...filtered].sort((a, b) => {
      const aIndex = COLUMN_ORDER.indexOf(a.name);
      const bIndex = COLUMN_ORDER.indexOf(b.name);
      const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (normalizedA !== normalizedB) {
        return normalizedA - normalizedB;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered;
  }, [columns, isLiveEvent]);

  const editableColumns = useMemo(() => {
    return visibleColumns.filter((column) => {
      const name = column.name;
      if (!name) return false;
      if (name === primaryKey) return false;
      return true;
    });
  }, [visibleColumns, primaryKey]);

  const iconOptions = useMemo(() => {
    const rowIcons = rows.map((row) => row.icon).filter(Boolean);
    const unique = Array.from(new Set(rowIcons));
    return unique.length > 0 ? unique : DEFAULT_ICON_OPTIONS;
  }, [rows]);

  const fetchPrizes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_PRIZES}?brand=${brand}`);

      if (!response.ok) {
        throw new Error('Failed to fetch lucky draw prizes');
      }

      const data = await response.json();
      setColumns(data.columns || []);
      setPrimaryKey(data.primaryKey || null);
      setRows(data.rows || []);

      const liveResponse = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_LIVE_EVENT}?brand=${brand}`);
      if (liveResponse.ok) {
        const liveData = await liveResponse.json();
        setIsLiveEvent(Boolean(liveData.is_live_event));
      }
    } catch (err) {
      console.error('Error fetching lucky draw prizes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrizes();
  }, [fetchPrizes]);

  const handleEditClick = (row) => {
    const initial = {};
    visibleColumns.forEach((column) => {
      initial[column.name] = normalizeEditValue(row[column.name], column.dataType);
    });
    setEditingRow({ original: row, values: initial });
    setEditDialogOpen(true);
  };

  const handleEditValueChange = (name, value) => {
    setEditingRow((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value
      }
    }));
  };

  const handleLiveEventToggle = async (checked) => {
    try {
      setSaving(true);
      setError(null);
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_LIVE_EVENT}?brand=${brand}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_live_event: checked })
      });

      if (!response.ok) {
        throw new Error('Failed to update live event mode');
      }

      setIsLiveEvent(checked);
      await fetchPrizes();
    } catch (err) {
      console.error('Error updating live event mode:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNewPrizeChange = (name, value) => {
    setNewPrize((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'quantity') {
        next.remaining_quantity = value;
      }
      return next;
    });
  };

  const openCreateDialog = () => {
    setNewPrize({
      label: '',
      icon: '🎁',
      probability: 0,
      quantity: 1,
      remaining_quantity: 1,
      is_claimable: false,
      is_active: true
    });
    setCreateDialogOpen(true);
  };

  const handleCreatePrize = async () => {
    try {
      setSaving(true);
      setError(null);
      const brand = getCurrentBrand();
      const payload = {
        ...newPrize,
        probability: Number(newPrize.probability),
        quantity: isLiveEvent && newPrize.quantity !== '' ? Number(newPrize.quantity) : null,
        remaining_quantity: isLiveEvent && newPrize.remaining_quantity !== '' ? Number(newPrize.remaining_quantity) : null,
        is_live_event: isLiveEvent
      };

      const response = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_PRIZES}?brand=${brand}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to create prize');
      }

      await fetchPrizes();
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Error creating prize:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editingRow || !primaryKey) return;

    try {
      setSaving(true);
      setError(null);
      const brand = getCurrentBrand();
      const payload = {};

      editableColumns.forEach((column) => {
        payload[column.name] = coerceEditValue(editingRow.values[column.name], column.dataType);
      });

      const rowId = editingRow.original[primaryKey];
      const response = await fetch(`${API_ENDPOINTS.LUCKY_DRAW_PRIZES}/${rowId}?brand=${brand}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update prize');
      }

      await fetchPrizes();
      setEditDialogOpen(false);
      setEditingRow(null);
    } catch (err) {
      console.error('Error updating prize:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderCellValue = (column, row) => {
    const value = row[column.name];

    if (column.name === 'icon') {
      return (
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: '#eef6ff',
            color: '#1f3a5f',
            width: 38,
            height: 38,
            borderRadius: 2.5,
            fontSize: '1rem',
            border: '1px solid rgba(52, 152, 219, 0.2)',
            boxShadow: '0 4px 10px rgba(52, 152, 219, 0.15)'
          }}
        >
          {value || '🎁'}
        </Avatar>
      );
    }

    if (column.name === 'is_active') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: value ? '#2ecc71' : '#e74c3c',
              boxShadow: value ? '0 0 0 2px rgba(46, 204, 113, 0.2)' : 'none'
            }}
          />
          <Typography
            variant="caption"
            fontWeight="700"
            sx={{
              color: value ? '#27ae60' : '#c0392b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            {value ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      );
    }

    if (column.name === 'is_claimable') {
      return (
        <Chip
          label={value ? 'Claimable' : 'Not claimable'}
          size="small"
          sx={{
            bgcolor: value ? 'rgba(201, 58, 58, 0.12)' : 'rgba(15, 23, 42, 0.08)',
            color: value ? '#b42318' : '#475569',
            fontWeight: 700,
            borderRadius: 2
          }}
        />
      );
    }

    if (column.name === 'probability') {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return (
          <Chip
            label={`${numeric.toFixed(2)}%`}
            size="small"
            sx={{
              bgcolor: 'rgba(52, 152, 219, 0.12)',
              color: '#1d4ed8',
              fontWeight: 600,
              borderRadius: 2,
              border: '1px solid rgba(52, 152, 219, 0.2)'
            }}
          />
        );
      }
    }

    if (column.name === 'quantity' || column.name === 'remaining_quantity') {
      return (
        <Chip
          label={value === null || value === undefined ? 'Unlimited' : value}
          size="small"
          sx={{
            bgcolor: column.name === 'remaining_quantity' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(15, 23, 42, 0.08)',
            color: column.name === 'remaining_quantity' ? '#b45309' : '#334155',
            fontWeight: 700,
            borderRadius: 2
          }}
        />
      );
    }

    return getDisplayValue(value);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 6, justifyContent: 'center' }}>
          <CircularProgress size={28} />
          <Typography variant="body1">Loading lucky draw prizes...</Typography>
        </Stack>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                  Lucky Draw Prizes
                </Typography>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  Manage prize entries for the current brand.
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    bgcolor: isLiveEvent ? 'rgba(245, 158, 11, 0.12)' : 'rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: isLiveEvent ? '#b45309' : '#475569' }}>
                    Live Event
                  </Typography>
                  <Switch
                    size="small"
                    checked={isLiveEvent}
                    onChange={(event) => handleLiveEventToggle(event.target.checked)}
                    disabled={saving}
                  />
                </Box>
                {isMillenniumBrand && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PeopleIcon />}
                    onClick={openMembersDialog}
                    sx={{
                      color: '#2c3e50',
                      borderColor: '#2c3e50',
                      '&:hover': {
                        bgcolor: 'rgba(44, 62, 80, 0.08)',
                        borderColor: '#1a252f'
                      },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    Check Members
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  sx={{
                    bgcolor: '#2c3e50',
                    '&:hover': { bgcolor: '#1a252f' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  Add Prize
                </Button>
                <Chip
                  label={`${rows.length} prizes`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(52, 152, 219, 0.12)',
                    color: '#1d4ed8',
                    fontWeight: 600
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: '#6c757d' }}>
                No prizes found for this brand.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer
                  sx={{
                    borderTop: '1px solid #eef2f7',
                    background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)'
                  }}
                >
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        {visibleColumns.map((column) => (
                          <TableCell
                            key={column.name}
                            sx={{
                              fontWeight: 700,
                              color: 'text.secondary',
                              py: 2,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              fontSize: '0.72rem'
                            }}
                          >
                            {formatHeader(column.name)}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: 'text.secondary',
                            py: 2,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            fontSize: '0.72rem'
                          }}
                          align="right"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row[primaryKey] || JSON.stringify(row)}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(52, 152, 219, 0.04)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }
                          }}
                        >
                          {visibleColumns.map((column) => (
                            <TableCell
                              key={column.name}
                              sx={{
                                color: '#2c3e50',
                                py: 2.5,
                                fontWeight: column.name === 'label' ? 600 : 500
                              }}
                            >
                              {renderCellValue(column, row)}
                            </TableCell>
                          ))}
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(row)}
                              disabled={!primaryKey}
                              sx={{
                                color: '#3498db',
                                bgcolor: 'rgba(52, 152, 219, 0.1)',
                                borderRadius: 2,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: '#3498db',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                <Stack spacing={2}>
                  {rows.map((row) => (
                    <Paper
                      key={row[primaryKey] || JSON.stringify(row)}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        background: '#ffffff'
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          {renderCellValue({ name: 'icon' }, row)}
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                              {row.label || row.name || 'Prize'}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {renderCellValue({ name: 'probability' }, row)}
                            </Box>
                          </Box>
                        </Stack>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(row)}
                          disabled={!primaryKey}
                          sx={{
                            color: '#3498db',
                            bgcolor: 'rgba(52, 152, 219, 0.1)',
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: '#3498db',
                              color: 'white'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        {renderCellValue({ name: 'is_active' }, row)}
                        {renderCellValue({ name: 'is_claimable' }, row)}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            color: 'white',
            fontWeight: 700,
            py: 2
          }}
        >
          Add Prize
        </DialogTitle>
        <DialogContent sx={{ pt: 10, pb: 1 }}>
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <TextField
              label="Label"
              value={newPrize.label}
              fullWidth
              onChange={(event) => handleNewPrizeChange('label', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={newPrize.icon || ''}
                label="Icon"
                onChange={(event) => handleNewPrizeChange('icon', event.target.value)}
                renderValue={(selected) => (selected ? selected : 'None')}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {iconOptions.map((icon) => (
                  <MenuItem key={icon} value={icon}>
                    <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Probability"
              value={newPrize.probability}
              fullWidth
              type="number"
              onChange={(event) => handleNewPrizeChange('probability', event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            {isLiveEvent && (
              <>
                <TextField
                  label="Quantity"
                  value={newPrize.quantity}
                  fullWidth
                  type="number"
                  onChange={(event) => handleNewPrizeChange('quantity', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Remaining Quantity"
                  value={newPrize.remaining_quantity}
                  fullWidth
                  type="number"
                  onChange={(event) => handleNewPrizeChange('remaining_quantity', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                />
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                px: 2.5,
                py: 2,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Claimable prize
                </Typography>
                <Typography variant="caption" sx={{ color: '#6c757d' }}>
                  Let members redeem this prize after they win it.
                </Typography>
              </Box>
              <Switch
                checked={Boolean(newPrize.is_claimable)}
                onChange={(event) => handleNewPrizeChange('is_claimable', event.target.checked)}
                color="primary"
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                px: 2.5,
                py: 2,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Active
                </Typography>
                <Typography variant="caption" sx={{ color: '#6c757d' }}>
                  Make this prize available.
                </Typography>
              </Box>
              <Switch
                checked={Boolean(newPrize.is_active)}
                onChange={(event) => handleNewPrizeChange('is_active', event.target.checked)}
                color="primary"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f8f9fa',
            borderTop: '1px solid #e2e8f0'
          }}
        >
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePrize}
            disabled={saving || !newPrize.label}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              bgcolor: '#2c3e50',
              '&:hover': { bgcolor: '#1a252f' },
              borderRadius: 2,
              px: 3
            }}
          >
            Save Prize
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            color: 'white',
            fontWeight: 700,
            py: 2
          }}
        >
          Update Prize
        </DialogTitle>
        <DialogContent sx={{ pt: 10, pb: 1 }}>
          {editingRow && (
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              {visibleColumns.map((column) => {
                const isReadOnly =
                  column.name === primaryKey;

                if (isReadOnly) {
                  return (
                    <TextField
                      key={column.name}
                      label={formatHeader(column.name)}
                      value={editingRow.values[column.name]}
                      fullWidth
                      disabled
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }

                if (column.name === 'icon') {
                  return (
                    <FormControl key={column.name} fullWidth>
                      <InputLabel>{formatHeader(column.name)}</InputLabel>
                      <Select
                        value={editingRow.values[column.name] || ''}
                        label={formatHeader(column.name)}
                        onChange={(event) => handleEditValueChange(column.name, event.target.value)}
                        renderValue={(selected) => (selected ? selected : 'None')}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {iconOptions.map((icon) => (
                          <MenuItem key={icon} value={icon}>
                            <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }

                if (isBooleanType(column.dataType)) {
                  return (
                    <Box
                      key={column.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        px: 2.5,
                        py: 2,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          {formatHeader(column.name)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6c757d' }}>
                          {column.name === 'is_claimable'
                            ? 'Members can redeem this prize after winning it.'
                            : 'Toggle active status for this prize.'}
                        </Typography>
                      </Box>
                      <Switch
                        checked={Boolean(editingRow.values[column.name])}
                        onChange={(event) => handleEditValueChange(column.name, event.target.checked)}
                        color="primary"
                      />
                    </Box>
                  );
                }

                return (
                  <TextField
                    key={column.name}
                    label={formatHeader(column.name)}
                    value={editingRow.values[column.name]}
                    fullWidth
                    multiline={isJsonType(column.dataType)}
                    minRows={isJsonType(column.dataType) ? 3 : 1}
                    type={isNumberType(column.dataType) ? 'number' : 'text'}
                    onChange={(event) => handleEditValueChange(column.name, event.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f8f9fa',
            borderTop: '1px solid #e2e8f0'
          }}
        >
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editingRow}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              bgcolor: '#2c3e50',
              '&:hover': { bgcolor: '#1a252f' },
              borderRadius: 2,
              px: 3
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check Members Dialog for Millennium brands */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '100%',
            maxWidth: '600px !important'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            color: 'white',
            fontWeight: 700,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            BMW Millennium Registered Members
          </Typography>
          <Chip
            label={`${filteredMembers.length} members`}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 700
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {membersError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {membersError}
            </Alert>
          )}

          <Box 
            sx={{ 
              p: 2, 
              mt: 2, 
              mb: 3, 
              borderRadius: 2, 
              bgcolor: '#f8fafc', 
              border: '1px solid #e2e8f0' 
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
                sx={{ bgcolor: 'white' }}
              />
              <TextField
                size="small"
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
                sx={{ bgcolor: 'white' }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{
                  bgcolor: '#3498db',
                  '&:hover': { bgcolor: '#2980b9' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  height: '40px',
                  boxShadow: '0 4px 12px rgba(52, 152, 219, 0.2)'
                }}
              >
                Search
              </Button>
            </Stack>
          </Box>

          {membersLoading || isSearching ? (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 6, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2">{isSearching ? 'Searching...' : 'Loading members...'}</Typography>
            </Stack>
          ) : filteredMembers.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No members found.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 400, overflowY: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569', py: 1.5, fontSize: '0.85rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569', py: 1.5, fontSize: '0.85rem' }} align="center">Points</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569', py: 1.5, fontSize: '0.85rem' }}>Registered Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMembers.map((member) => (
                    <TableRow key={member.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 600, py: 1.5, color: '#1e293b' }}>{member.name || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Chip
                          label={member.points || 0}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(46, 204, 113, 0.15)',
                            color: '#27ae60',
                            fontWeight: 700
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5, color: '#64748b', fontWeight: 500 }}>
                        {formatRegisterDate(member.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setMembersDialogOpen(false)} variant="contained" sx={{ bgcolor: '#2c3e50', '&:hover': { bgcolor: '#1a252f' }, borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LuckyDrawPrizes;
